import React, { FC, JSX, useEffect, useRef, useState } from "react";
import { ColumnConfig, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ProgressBar } from "@app/shared/ProgressBar";
import { QESWidget, QESWidgetContent, QESWidgetHeader, QESWidgetTitle } from "@app/shared/widget";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";
import ComplianceConnectService from "@app/compliance-connect/common/complianceConnectService";
import { IssuerComplianceComparison, TrendDirection } from "@app/compliance-connect/regulator/complianceConnectTypes";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesColors } from "@app/shared/qes-standards/colors";
import { WafflesThemes } from "../../../shared/waffles/exports";

interface IssuerComplianceComparisonWidgetProps {
    networkIds: string[];
}

interface IssuerComplianceComparisonTableData {
    adequacyIndexScore: string;
    adequacyScoreBar: JSX.Element;
    approved: JSX.Element;
    denied: JSX.Element;
    exceptionOverlap: JSX.Element;
    exceptions: JSX.Element;
    gapOverlap: JSX.Element;
    gaps: JSX.Element;
    issuer: JSX.Element;
    pending: JSX.Element;
    rank: JSX.Element;
    topCountyGap: JSX.Element;
    topSpecialtyGap: JSX.Element;
    [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
    adequacyIndexScore: { alignment: TextAlignment.Left, displayText: "Adequacy Index",    minWidth: "55px",  noSort: true, sortOrder: 4,  testId: "adequacy-index-score" },
    adequacyScoreBar:   { alignment: TextAlignment.Left, displayText: "Adequacy",          minWidth: "120px", noSort: true, sortOrder: 3,  testId: "adequacy-score-bar" },
    approved:           { alignment: TextAlignment.Left, displayText: "Approved",          minWidth: "55px",  noSort: true, sortOrder: 7,  testId: "approved" },
    denied:             { alignment: TextAlignment.Left, displayText: "Denied",            minWidth: "45px",  noSort: true, sortOrder: 8,  testId: "denied" },
    exceptionOverlap:   { alignment: TextAlignment.Left, displayText: "Exception Overlap", minWidth: "75px",  noSort: true, sortOrder: 11, testId: "exception-overlap" },
    exceptions:         { alignment: TextAlignment.Left, displayText: "Exceptions",        minWidth: "60px",  noSort: true, sortOrder: 6,  testId: "exceptions" },
    gapOverlap:         { alignment: TextAlignment.Left, displayText: "Gap Overlap",       minWidth: "60px",  noSort: true, sortOrder: 10, testId: "gap-overlap" },
    gaps:               { alignment: TextAlignment.Left, displayText: "Gaps",              minWidth: "45px",  noSort: true, sortOrder: 5,  testId: "gaps" },
    issuer:             { alignment: TextAlignment.Left, displayText: "Issuer",            minWidth: "80px",  noSort: true, sortOrder: 2,  testId: "issuer" },
    pending:            { alignment: TextAlignment.Left, displayText: "Pending",           minWidth: "50px",  noSort: true, sortOrder: 9,  testId: "pending" },
    rank:               { alignment: TextAlignment.Left, displayText: "Rank",              minWidth: "40px",  noSort: true, sortOrder: 1,  testId: "rank" },
    topCountyGap:       { alignment: TextAlignment.Left, displayText: "Top County Gap",    minWidth: "75px",  noSort: true, sortOrder: 12, testId: "top-county-gap" },
    topSpecialtyGap:    { alignment: TextAlignment.Left, displayText: "Top Specialty Gap", minWidth: "80px",  noSort: true, sortOrder: 13, testId: "top-specialty-gap" },
};

const noopClick = (e: React.MouseEvent) => e.preventDefault();

const trendArrow = (direction: TrendDirection, after = false): JSX.Element => {
    const className = after ? "ms-1" : "me-1";
    const margin = after ? { marginLeft: "0.25rem" } : { marginRight: "0.25rem" };
    if (direction === TrendDirection.Up)
        return <QesIcon className={className} color={QesColors.QesPrimaryRed} description="Increased" iconName={QesIcons.ArrowNarrowUp} />;
    if (direction === TrendDirection.Down)
        return <QesIcon className={className} color={QesColors.QesPrimaryRed} description="Decreased" iconName={QesIcons.ArrowNarrowDown} />;
    return <span style={{ display: "inline-block", width: "1.1em", ...margin }} />;
};

const numericCell = (value: number, trend?: TrendDirection): JSX.Element => {
    const arrow = trend !== undefined ? trendArrow(trend) : null;
    return value === 0
        ? <span>{arrow}{value}</span>
        : <a href="#" onClick={noopClick}>{arrow}{value}</a>;
};

const stringCell = (value: string): JSX.Element =>
    !value?.trim() ? <span>—</span> : <a href="#" onClick={noopClick}>{value}</a>;

const toTableRow = (issuer: IssuerComplianceComparison): IssuerComplianceComparisonTableData => ({
    adequacyIndexScore: (issuer.adequacyIndex * 100).toFixed(1),
    adequacyScoreBar: (
        <div style={{ paddingRight: "1rem" }}>
            <ProgressBar
                bands={[
                    { color: QesColors.QesPrimaryGreen,    colorType: "hex", label: "Meets Both Criteria",                       outOfScale: false, percent: issuer.percentPassBoth },
                    { color: QesColors.QesTertiaryYellow2, colorType: "hex", label: "Meets One of Access or Servicing Criteria", outOfScale: false, percent: issuer.percentPassAccessOnly + issuer.percentPassServicingOnly },
                    { color: QesColors.QesPrimaryRed,      colorType: "hex", label: "Does Not Meet Any Criteria",                outOfScale: false, percent: issuer.percentPassNone },
                    { color: QesColors.Blue500,     colorType: "hex", label: "Passes by Exception",                       outOfScale: false, percent: issuer.percentPassByException },
                ]}
                isGrayscale={false}
            />
        </div>
    ),
    approved:         numericCell(issuer.approved),
    denied:           numericCell(issuer.denied,      issuer.deniedTrend),
    exceptionOverlap: numericCell(issuer.exceptionOverlap),
    exceptions:       numericCell(issuer.exceptions,  issuer.exceptionsTrend),
    gapOverlap:       numericCell(issuer.gapOverlap),
    gaps:             numericCell(issuer.gaps,         issuer.gapsTrend),
    issuer:           <a href="#" onClick={noopClick}>{issuer.issuerName}</a>,
    pending:          numericCell(issuer.pending),
    rank:             <span>{issuer.rank}{trendArrow(issuer.rankTrend, true)}</span>,
    topCountyGap:     stringCell(issuer.topCountyGap),
    topSpecialtyGap:  stringCell(issuer.topSpecialtyGap),
    [wafflesKeyColumnName]: issuer.issuerId,
});

const IssuerComplianceComparisonWidget: FC<IssuerComplianceComparisonWidgetProps> = ({ networkIds }) => {
    const service = useRef(new ComplianceConnectService()).current;
    const [issuers, setIssuers] = useState<IssuerComplianceComparison[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError(false);
        service.getIssuerComplianceComparison(networkIds)
            .then(data => setIssuers(data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [networkIds, service]);

    const renderContent = () => {
        if (loading) return <LoadingSpinner />;
        if (error) return <QESAlert type="danger" persist>{getGenericErrorMessage()}</QESAlert>;

        return (
            <div className="pt-3">
                <Waffles<IssuerComplianceComparisonTableData>
                    columns={columnConfig}
                    data={issuers.map(toTableRow)}
                    fullWidth={true}
                    rowDecorator={() => "border-bottom"}
                    showCheckboxes={false}
                    testId="compliance-connect__issuer-compliance-comparison-table"
                    theme={WafflesThemes.Simple}
                />
            </div>
        );
    };

    return (
        <div data-testid="compliance-connect__issuer-compliance-comparison-widget">
            <QESWidget className="w-100">
                <QESWidgetHeader>
                    <QESWidgetTitle>Issuer Compliance Comparison</QESWidgetTitle>
                </QESWidgetHeader>
                <QESWidgetContent>
                    {renderContent()}
                </QESWidgetContent>
            </QESWidget>
        </div>
    );
};

export default IssuerComplianceComparisonWidget;
